import { FormattedMessage, IntlShape } from "react-intl";
import { Column } from "react-table";
import { useNavigate } from "react-router-dom";

import Button from "components/atoms/Button/Button";
import { getFreightModeIcon } from "helpers/freightModeHelper";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { formatNumber } from "helpers/commonHelpers";
import Badge from "components/atoms/Badge/Badge";
import { DateTimeFormat, formatDate } from "helpers/dateTimeHelper";
import moment from "moment";
import { InvoiceStatusColors } from "../../../constants";

const getInvoiceColumns = (intl: IntlShape, navigate: ReturnType<typeof useNavigate>) => {
  const columns: Column[] = [
    {
      id: "shipment",
      Header: intl.formatMessage({
        id: "general.shipment",
        defaultMessage: "Shipment",
      }),
      accessor: "shipment_reference",
      sortable: true,
      Cell: ({ value, row }: { value: string; row: any }) => (
        <>
          <p className="font-b4 mb-1">
            {value || "-"} / {row.original?.invoice_reference}
          </p>
          <span>{row?.original?.shipment_title || "-"}</span>
        </>
      ),
    },
    {
      Header: intl.formatMessage({
        id: "general.shipper",
        defaultMessage: "Shipper",
      }),
      accessor: "shipper",
      sortable: true,
      Cell: ({ value, row }: { value: string; row: any }) => (
        <>
          <p className="font-b4 mb-1">{value || "-"}</p>
          <span>{row?.original?.payment_location || "-"}</span>
        </>
      ),
    },
    {
      Header: intl.formatMessage({
        id: "general.team",
        defaultMessage: "Team",
      }),
      accessor: "team_reference",
      sortable: true,
      Cell: ({ row }: any) => <div>{row?.original?.team_name || "-"}</div>,
    },
    {
      Header: intl.formatMessage({
        id: "general.freightMode",
        defaultMessage: "Freight mode",
      }),
      accessor: "freight_mode",
      sortable: true,
      Cell: ({ value, row }: any) => {
        const Icon = getFreightModeIcon(value, row.original?.freight_mode_type, true);
        return (
          <div>
            {value ? (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="freight-mode">
                    <span className="font-weight-normal">{row.original?.freight_mode}</span>
                  </Tooltip>
                }
              >
                {value && <Icon />}
              </OverlayTrigger>
            ) : (
              "-"
            )}
          </div>
        );
      },
    },
    {
      Header: intl.formatMessage({
        id: "general.amount",
        defaultMessage: "Amount",
      }),
      accessor: "total_invoice_amount",
      sortable: true,
      Cell: ({ value, row }: { value: number; row: any }) => {
        const expectedAmountInUSD = row.original?.expected_amount_due_usd;
        const actualAmountInUSD = row.original?.total_quoted_amount_usd;
        const { is_shipment_canceled } = row?.original || {};
        const changeInValue = expectedAmountInUSD - actualAmountInUSD;
        const color = changeInValue > 0 ? "#EA7939" : "#9EC728";
        const prefix = changeInValue > 0 ? "+" : "";

        let percentageDiff = null;
        if (changeInValue !== 0 && expectedAmountInUSD !== 0) {
          percentageDiff = (
            <span className="font-weight-normal ml-2 font-b3" style={{ color }}>
              ({prefix}
              {((changeInValue / actualAmountInUSD) * 100).toFixed(1)}%)
            </span>
          );
        }

        return (
          <>
            <div className="mb-1">
              {formatNumber(value, 2)} {row.original?.invoice_currency}
              {percentageDiff}
            </div>

            {is_shipment_canceled && (
              <p className="text-danger">
                <FormattedMessage id="invoice.shipmentCancelled" defaultMessage="Shipment cancelled" />
              </p>
            )}
          </>
        );
      },
    },
    {
      Header: intl.formatMessage({
        id: "quote.listing.fromTo",
        defaultMessage: "From-to",
      }),
      accessor: "origin",
      sortable: true,
      Cell: ({ value, row }: { value: string; row: any }) => (
        <div>
          {value} {intl.formatMessage({ id: "general.to", defaultMessage: "to" })} {row.original?.destination}
        </div>
      ),
    },
    {
      Header: intl.formatMessage({
        id: "invoice.listing.totalInvoices",
        defaultMessage: "Total invoices",
      }),
      accessor: "total_invoice_count",
      sortable: true,
      Cell: ({ value }: { value: number }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({
        id: "invoice.listing.podDate",
        defaultMessage: "POD date",
      }),
      accessor: "pod",
      sortable: true,
      sortType: (rowA: any, rowB: any, columnId: any) => {
        const dateA = moment(rowA.values[columnId], "DD MMM YYYY", "en");
        const dateB = moment(rowB.values[columnId], "DD MMM YYYY", "en");
        return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
      },
      Cell: ({ value }: { value: number }) => <div>{value ? formatDate(value, DateTimeFormat.dateFormat) : "-"}</div>,
    },
    {
      Header: intl.formatMessage({
        id: "invoice.listing.invoiceSubmitDate",
        defaultMessage: "Invoice submit date",
      }),
      accessor: "invoice_submit_date",
      sortable: true,
      Cell: ({ value }: { value: string }) => (
        <div>{value ? formatDate(value, DateTimeFormat.fullDateTimeFormat) : "-"}</div>
      ),
    },
    {
      Header: intl.formatMessage({
        id: "invoice.listing.invoiceLastModifiedDate",
        defaultMessage: "Invoice last modified date",
      }),
      accessor: "invoice_last_modified_date",
      sortable: true,
      Cell: ({ value }: { value: string }) => (
        <div>{value ? formatDate(value, DateTimeFormat.fullDateTimeFormat) : "-"}</div>
      ),
    },
    {
      Header: intl.formatMessage({
        id: "invoice.listing.invoiceApprovedDate",
        defaultMessage: "Invoice approved date",
      }),
      accessor: "invoice_approved_date",
      sortable: true,
      Cell: ({ value }: { value: string }) => (
        <div>{value ? formatDate(value, DateTimeFormat.fullDateTimeFormat) : "-"}</div>
      ),
    },
    {
      Header: intl.formatMessage({
        id: "general.status",
        defaultMessage: "Status",
      }),
      accessor: "display_status",
      Cell: ({ value }: any) =>
        value ? (
          <div className="d-flex flex-column align-items-start">
            <Badge type={InvoiceStatusColors[value?.value] || "secondary"} label={value?.label} />
          </div>
        ) : (
          "-"
        ),
    },
    {
      Header: "",
      accessor: "shipment_reference",
      actions: true,
      disableDrag: true,
      Cell: ({ row }: any) => (
        <>
          {row.original?.display_status?.value === "Unsubmitted" && (
            <Button
              label={intl.formatMessage({
                id: "general.submit",
                defaultMessage: "Submit",
              })}
              onClick={() =>
                navigate(`/shipment/${row.original?.shipment_reference}/billing?invoice=${row.original?.invoice_reference}`)
              }
            />
          )}
        </>
      ),
    },
  ];

  return columns;
};

export default getInvoiceColumns;
